import styled, { css } from 'styled-components'
import { themeColor, themeFontFamily, themeFontWeight } from 'LibNeo'
import { Select } from 'antd'
import { WrapperInput } from 'LibNeo/components/Basic/Input/Input/styled'

export const StyledAntdSelect = styled(Select)<{
	hasError: boolean
	requiredHighlight?: boolean
}>`
	width: 100%;
	font-family: ${themeFontFamily('SBSansInterface')};
	font-size: 14px;
	line-height: 20px;
	color: ${themeColor('extraBackground3')};

	&.ant-select-open {
		.ant-select-selection {
			border: 1px solid ${themeColor('emerald', 6)};
			box-shadow: none;
		}
	}

	&.ant-select-disabled {
		opacity: unset;
		background-color: ${themeColor('gray', 1)};
	}

	.ant-select-selection {
		padding: 9px 40px 9px 16px;
		border-radius: 6px;
		outline: none;
		border: 1px solid ${themeColor('gray', 3)};
		box-shadow: none;
		${({ requiredHighlight }) =>
			requiredHighlight &&
			css`
				border: 1px solid ${themeColor('sun')};
			`}
		${({ hasError }) =>
			hasError &&
			css`
				border: 1px solid ${themeColor('pink', 5)};
			`}

		&:focus {
			border: 1px solid ${({ hasError }) => themeColor(hasError ? 'pink' : 'emerald', 5)};
			box-shadow: none;
			${({ requiredHighlight }) =>
				requiredHighlight &&
				css`
					border: 1px solid ${themeColor('sun')};
				`}
		}
		&:hover {
			border: 1px solid ${({ hasError }) => themeColor(hasError ? 'pink' : 'gray', 5)};
			box-shadow: none;
		}

		input {
			margin-top: -16px;
			padding-left: 0;
		}

		&--single,
		&--multiple {
			height: auto;
			min-height: 40px;
			.ant-select-selection__rendered,
			&__rendered {
				margin-right: 0;
				margin-left: 0;
			}
		}

		&--single {
			.ant-select-selection__rendered,
			&__rendered {
				line-height: 20px;
			}
		}

		&__placeholder {
			color: ${themeColor('gray', 4)};
			font-family: ${themeFontFamily('SBSansInterface')};
			font-size: 14px;
			line-height: 20px;
		}

		.ant-select-arrow {
			top: calc(50% - 4px);
		}

		.ant-select-clear-icon {
			position: relative;
			right: 8px;
			top: -4px;
			background-color: ${themeColor('white')};
		}

		&--multiple {
			padding: 5px 40px 5px 16px;
			line-height: 0;

			.ant-select-selection__placeholder {
				margin-left: 0;
			}
			.ant-select-selection__clear {
				top: 50%;
			}

			.ant-select-selection__choice {
				background-color: ${themeColor('arctic', 1)};
				color: ${themeColor('gray', 8)};
				border: none;
				height: 20px;
				font-family: ${themeFontFamily('SBSansInterface')};
				font-size: 12px;
				line-height: 20px;
				padding: 0 20px 0 6px;
				margin: 4px 8px 4px 0;
				&__remove {
					transform: none;
				}
				&__disabled {
					background-color: ${themeColor('gray', 1)};
					color: ${themeColor('gray', 5)};
					padding-right: 6px;
					cursor: initial;
				}
				&:hover:not(&__disabled) {
					background-color: ${themeColor('arctic', 2)};
					cursor: pointer;
				}
			}
		}
	}

	.ant-select-remove-icon {
		vertical-align: middle;
	}

	.ant-calendar-picker-icon {
		margin-top: 0;
	}

	input {
		padding: 20px;
		border-radius: 8px;
		margin-top: 16px;
	}
`

export const StyledSelectAntdOption = styled(Select.Option)`
	font-family: ${themeFontFamily('SBSansInterface')};
	font-size: 14px;
	line-height: 20px;
	color: ${themeColor('extraBackground3')};
	padding: 10px 40px 10px 16px;

	& > div {
		display: flex;
		justify-content: space-between;
	}

	&--other {
		padding: 4px 12px 4px 16px;
		${WrapperInput} {
			display: inline-block;
			margin-left: 8px;
			width: calc(100% - 46px);
			min-height: auto;
			padding: 6px 12px;
		}
	}

	&--select {
		color: ${themeColor('emerald', 7)};

		${WrapperInput} {
			color: ${themeColor('emerald', 7)};
		}
	}
`

export const PrependLine = styled.div`
	position: relative;
	height: 20px;
	margin-bottom: 4px;
`

export const ErrorText = styled.div<{ hasError: boolean }>`
	position: absolute;
	color: ${themeColor('pink', 5)};
	font-family: ${themeFontFamily('SBSansInterface')};
	font-size: 12px;
	line-height: 16px;
	padding: 4px 0;
	display: ${({ hasError }) => (hasError ? 'block' : 'none')};
	${({ hasError }) => hasError && `top: 64px ;`}
	transition: all 0.3s linear;
`

export const SelectWrapper = styled.div<{ width?: number }>`
	position: relative;
	width: 100%;

	.ant-form-item-control {
		line-height: 0;
	}
	.ant-select-dropdown {
		margin: 4px 0;
		border: 1px solid ${themeColor('gray', 3)};
		box-shadow: ${themeColor('dropdownShadow')};
		border-radius: 6px;
		${({ width }) =>
			width &&
			css`
				width: ${width}px !important;
			`}
	}
	.ant-select-dropdown-menu {
		padding: 0;
		max-height: 240px;
		li:first-child {
			margin-top: 8px;
		}
		li:last-child {
			margin-bottom: 8px;
		}
	}

	.ant-select-dropdown-menu-item {
		white-space: initial;
		text-overflow: initial;
		padding: 10px 40px 10px 16px;
		color: ${themeColor('gray', 8)};
		font-family: ${themeFontFamily('SBSansInterface')};
		font-size: 14px;
		line-height: 20px;
		&:hover {
			background-color: ${themeColor('gray', 1)};
		}
	}

	.ant-select-dropdown-menu-item-selected {
		color: ${themeColor('emerald', 5)};
		background-color: ${themeColor('white')};
		font-weight: ${themeFontWeight('SBSansInterface', 'regular')};

		.anticon.anticon-check.ant-select-selected-icon {
			color: ${themeColor('emerald', 5)};
		}
	}

	.ant-select-dropdown-menu-item-disabled {
		color: ${themeColor('gray', 4)};

		.anticon.anticon-check.ant-select-selected-icon {
			color: ${themeColor('gray', 4)};
		}
	}

	.ant-select-selection-selected-value {
		color: ${themeColor('gray', 8)};
	}

	.has-error .ant-form-explain {
		min-height: unset;
		margin: 0;
		padding: 4px 0;
		font-family: ${themeFontFamily('SBSansInterface')};
		font-size: 12px;
		line-height: 16px;
		font-weight: ${themeFontWeight('SBSansInterface', 'regular')};
		color: ${themeColor('pink', 5)};
	}

	.ant-select-dropdown--empty {
		li {
			padding: 2px 40px 2px 16px;
		}
		.p {
			margin-bottom: 0;
		}
	}

	.ant-select-dropdown-menu-item-active {
		background-color: ${themeColor('gray', 1)};
	}
`
